import { createI18n } from 'vue-i18n'
import { zhHans, zhHant } from 'vuetify/locale'

/**
 * 語系來源與後端 SetLocale middleware 共用同一個 cookie（名稱 locale，值 zh-Hant / zh-Hans）。
 * 前端切換時只要改 locale，watcher 會把 cookie 寫回去，後端下一個 request 就會用新語系回應。
 * cookie 名稱或值的格式改了，後端也要一起改，否則前後端語系會對不上。
 */
export const SUPPORTED_LOCALES = ['zh-Hant', 'zh-Hans']
const COOKIE_KEY = 'locale'
const DEFAULT_LOCALE = 'zh-Hant'

const vuetifyMessages = {
  'zh-Hant': zhHant,
  'zh-Hans': zhHans,
}

const messages = Object.fromEntries(Object.entries(import.meta.glob('./locales/*.json', { eager: true }))
  .map(([key, value]) => {
    const locale = key.slice(10, -5)

    return [locale, { ...value.default, $vuetify: vuetifyMessages[locale] }]
  }))

function readCookie(name) {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name, value, days = 365) {
  if (typeof document === 'undefined') return
  const d = new Date()
  d.setTime(d.getTime() + days * 86400000)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
}

/**
 * 依瀏覽器語言偏好偵測。中文系列細分：
 *   zh-CN / zh-SG / zh-MY / *Hans* → 簡中
 *   其餘中文（zh-TW / zh-HK / zh-MO / *Hant* / 單純 zh）→ 繁中
 */
function detectBrowserLocale() {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const langs = navigator.languages || [navigator.language || '']
  for (const raw of langs) {
    const lang = raw.toLowerCase()
    if (
      lang === 'zh-cn' || lang.startsWith('zh-cn-') ||
      lang === 'zh-sg' || lang.startsWith('zh-sg-') ||
      lang === 'zh-my' || lang.startsWith('zh-my-') ||
      lang.includes('hans')
    ) {
      return 'zh-Hans'
    }
    if (lang.startsWith('zh')) {
      return DEFAULT_LOCALE
    }
  }
  return DEFAULT_LOCALE
}

function resolveInitialLocale() {
  const cookie = readCookie(COOKIE_KEY)
  if (SUPPORTED_LOCALES.includes(cookie)) return cookie

  return detectBrowserLocale()
}

let _i18n = null
export const getI18n = () => {
  if (_i18n === null) {
    const locale = resolveInitialLocale()

    _i18n = createI18n({
      legacy: false,
      locale,
      fallbackLocale: DEFAULT_LOCALE,
      messages,
    })

    // 第一次載入也寫 cookie，後端 middleware 第一個 request 就能拿到一致的語系
    if (typeof document !== 'undefined') {
      if (readCookie(COOKIE_KEY) !== locale) writeCookie(COOKIE_KEY, locale)
      document.documentElement.lang = locale
    }

    watch(_i18n.global.locale, val => {
      if (!SUPPORTED_LOCALES.includes(val)) return
      writeCookie(COOKIE_KEY, val)
      if (typeof document !== 'undefined') document.documentElement.lang = val
    })
  }

  return _i18n
}

export default function (app) {
  app.use(getI18n())
}
